/**
 * PokerEngine — Core game logic for Texas Hold'em Poker
 *
 * Manages complete game state: deck, dealing, betting rounds (Pre-Flop,
 * Flop, Turn, River), hand evaluation, pot/side-pot calculation, showdown
 * resolution, player elimination, and dealer button rotation.
 *
 * Requirements: 2.1–2.7, 3.1–3.5, 4.1–4.7, 5.1–5.5, 6.1–6.5, 7.1–7.5,
 *               8.1–8.8, 9.1–9.7, 10.1–10.6, 11.1–11.5, 32.1–32.4
 */

import {
  Card,
  Deck,
  PlayerState,
  PlayerStatus,
  PlayerAction,
  ActionType,
  ValidAction,
  ActionResult,
  ActionRecord,
  PotInfo,
  HandPhase,
  BettingRound,
  BlindLevel,
  PokerGameConfig,
  PokerGameState,
  PokerGameStatus,
  VisibleGameState,
  VisiblePlayerState,
  HandResult,
  HandSummary,
  ShowdownResult,
  ShowdownPlayerResult,
  PotDistribution,
  CommunityCardHistory,
  DEFAULT_BLIND_LEVEL,
  DEFAULT_BUY_IN,
} from './types';

import { createDeck, shuffleDeck, dealCard, burnCard } from './Deck';
import { evaluate, compare, findBestHand, describeHand } from './HandEvaluator';

export class PokerEngine {
  private state: PokerGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // ========================================================================
  // Game Initialization (Task 4)
  // ========================================================================

  /**
   * Initialize a new poker game with the given configuration.
   * Sets up table with 2–8 players, assigns seats, initial chip stacks,
   * and places dealer button at a random seat.
   */
  initializeGame(config: PokerGameConfig): void {
    const { tableSize, buyIn, blindLevel, players } = config;

    if (players.length < 2 || players.length > 8) {
      throw new Error(`Invalid player count: ${players.length}. Must be 2–8.`);
    }
    if (players.length > tableSize) {
      throw new Error(`Too many players (${players.length}) for table size ${tableSize}.`);
    }

    const playerStates: PlayerState[] = players.map((p, i) => ({
      id: p.id,
      name: p.name,
      seatIndex: i,
      chipStack: buyIn,
      holeCards: [],
      status: 'active' as PlayerStatus,
      currentBet: 0,
      totalBetThisHand: 0,
      isDealer: false,
      isSmallBlind: false,
      isBigBlind: false,
      isHuman: p.isHuman,
      aiDifficulty: p.aiDifficulty,
      hasActedThisRound: false,
      showCards: false,
    }));

    // Random dealer for first hand
    const dealerIdx = Math.floor(Math.random() * playerStates.length);

    this.state = {
      tableSize,
      buyIn,
      blindLevel,
      players: playerStates,
      dealerSeatIndex: dealerIdx,
      smallBlindSeatIndex: -1,
      bigBlindSeatIndex: -1,
      handNumber: 0,
      handPhase: 'pre-deal',
      deck: createDeck(),
      communityCards: [],
      pots: [],
      currentBettingRound: null,
      currentPlayerIndex: -1,
      lastRaiseAmount: 0,
      actionHistory: [],
      gameStatus: { type: 'active' },
      handHistory: [],
    };
  }

  /**
   * Start a new hand: rotate dealer, post blinds, shuffle, deal hole cards.
   */
  startNewHand(): void {
    if (this.state.gameStatus.type === 'complete') {
      throw new Error('Game is already complete.');
    }

    const activePlayers = this.getActivePlayersList();
    if (activePlayers.length < 2) {
      throw new Error('Need at least 2 active players to start a hand.');
    }

    this.state.handNumber++;
    this.state.handPhase = 'pre-flop';
    this.state.gameStatus = { type: 'handInProgress' };
    this.state.communityCards = [];
    this.state.actionHistory = [];
    this.state.lastRaiseAmount = this.state.blindLevel.bigBlind;

    // Reset player hand state
    for (const p of this.state.players) {
      p.holeCards = [];
      p.currentBet = 0;
      p.totalBetThisHand = 0;
      p.hasActedThisRound = false;
      p.showCards = false;
      p.isDealer = false;
      p.isSmallBlind = false;
      p.isBigBlind = false;
      if (p.status === 'folded' || p.status === 'all-in') {
        // Reset non-eliminated players to active for new hand
        if (p.chipStack > 0) {
          p.status = 'active';
        }
      }
    }

    // Rotate dealer
    if (this.state.handNumber === 1) {
      // First hand: dealer already set randomly in initializeGame
    } else {
      this.state.dealerSeatIndex = this.nextActiveSeat(this.state.dealerSeatIndex);
    }

    const dealerPlayer = this.state.players[this.state.dealerSeatIndex];
    dealerPlayer.isDealer = true;

    // Assign blinds
    this.assignBlinds(activePlayers);

    // Shuffle and deal
    this.state.deck = shuffleDeck(createDeck());
    this.dealHoleCards(activePlayers);

    // Post blinds
    this.postBlinds();

    // Initialize pots
    this.state.pots = [];

    // Set current betting round
    this.state.currentBettingRound = 'pre-flop';

    // Set first player to act: left of BB (Under the Gun)
    this.state.currentPlayerIndex = this.getFirstToActPreFlop();
  }

  // ========================================================================
  // Blind and Dealer Helpers
  // ========================================================================

  private assignBlinds(activePlayers: PlayerState[]): void {
    if (activePlayers.length === 2) {
      // Heads-up: dealer is SB, other player is BB
      const dealerPlayer = this.state.players[this.state.dealerSeatIndex];
      dealerPlayer.isSmallBlind = true;
      this.state.smallBlindSeatIndex = this.state.dealerSeatIndex;

      const bbIdx = this.nextActiveSeat(this.state.dealerSeatIndex);
      this.state.players[bbIdx].isBigBlind = true;
      this.state.bigBlindSeatIndex = bbIdx;
    } else {
      // Normal: SB is first active left of dealer, BB is next
      const sbIdx = this.nextActiveSeat(this.state.dealerSeatIndex);
      this.state.players[sbIdx].isSmallBlind = true;
      this.state.smallBlindSeatIndex = sbIdx;

      const bbIdx = this.nextActiveSeat(sbIdx);
      this.state.players[bbIdx].isBigBlind = true;
      this.state.bigBlindSeatIndex = bbIdx;
    }
  }

  private postBlinds(): void {
    const sb = this.state.players[this.state.smallBlindSeatIndex];
    const bb = this.state.players[this.state.bigBlindSeatIndex];
    const { smallBlind, bigBlind } = this.state.blindLevel;

    // Post small blind
    const sbAmount = Math.min(smallBlind, sb.chipStack);
    sb.chipStack -= sbAmount;
    sb.currentBet = sbAmount;
    sb.totalBetThisHand = sbAmount;
    if (sb.chipStack === 0) {
      sb.status = 'all-in';
    }

    // Post big blind
    const bbAmount = Math.min(bigBlind, bb.chipStack);
    bb.chipStack -= bbAmount;
    bb.currentBet = bbAmount;
    bb.totalBetThisHand = bbAmount;
    if (bb.chipStack === 0) {
      bb.status = 'all-in';
    }
  }

  private dealHoleCards(activePlayers: PlayerState[]): void {
    // Deal one card at a time to each player, starting left of dealer, two rounds
    const orderedPlayers = this.getPlayersInDealOrder();

    for (let round = 0; round < 2; round++) {
      for (const player of orderedPlayers) {
        const result = dealCard(this.state.deck);
        player.holeCards.push(result.card);
        this.state.deck = result.deck;
      }
    }
  }

  private getPlayersInDealOrder(): PlayerState[] {
    // Starting left of dealer, clockwise, only active (non-eliminated) players
    const result: PlayerState[] = [];
    let idx = this.nextActiveSeat(this.state.dealerSeatIndex);
    const startIdx = idx;
    do {
      result.push(this.state.players[idx]);
      idx = this.nextActiveSeat(idx);
    } while (idx !== startIdx);
    return result;
  }

  /**
   * Find the next active (non-eliminated) seat clockwise from the given index.
   */
  private nextActiveSeat(fromIndex: number): number {
    const n = this.state.players.length;
    let idx = (fromIndex + 1) % n;
    let safety = 0;
    while (this.state.players[idx].status === 'eliminated' && safety < n) {
      idx = (idx + 1) % n;
      safety++;
    }
    return idx;
  }

  /**
   * Pre-flop: first to act is left of BB (Under the Gun).
   * In heads-up, first to act pre-flop is the dealer/SB.
   */
  private getFirstToActPreFlop(): number {
    const activePlayers = this.getActivePlayersList();
    if (activePlayers.length === 2) {
      // Heads-up: dealer/SB acts first pre-flop
      return this.state.smallBlindSeatIndex;
    }
    // UTG: next active player after BB
    return this.nextActiveNonAllInSeat(this.state.bigBlindSeatIndex);
  }

  /**
   * Post-flop: first to act is first active non-all-in player left of dealer.
   */
  private getFirstToActPostFlop(): number {
    return this.nextActiveNonAllInSeat(this.state.dealerSeatIndex);
  }

  /**
   * Find next active, non-folded, non-all-in, non-eliminated seat.
   */
  private nextActiveNonAllInSeat(fromIndex: number): number {
    const n = this.state.players.length;
    let idx = (fromIndex + 1) % n;
    let safety = 0;
    while (safety < n) {
      const p = this.state.players[idx];
      if (p.status === 'active') {
        return idx;
      }
      idx = (idx + 1) % n;
      safety++;
    }
    // All players are all-in or folded — return -1
    return -1;
  }

  // ========================================================================
  // Betting Actions (Task 5)
  // ========================================================================

  /**
   * Get valid actions for the given player.
   * At minimum, fold and all-in are always available when it's a player's turn.
   */
  getValidActions(playerId: string): ValidAction[] {
    const player = this.findPlayer(playerId);
    if (!player) return [];
    if (this.state.currentPlayerIndex < 0) return [];
    if (this.state.players[this.state.currentPlayerIndex].id !== playerId) return [];
    if (player.status !== 'active') return [];

    const actions: ValidAction[] = [];
    const highestBet = this.getHighestCurrentBet();
    const toCall = highestBet - player.currentBet;
    const { bigBlind } = this.state.blindLevel;

    // Fold is always available
    actions.push({ type: 'fold' });

    // Check: available when no bet to call
    if (toCall === 0) {
      actions.push({ type: 'check' });
    }

    // Call: available when there's a bet to match and player has enough chips
    if (toCall > 0 && player.chipStack > 0) {
      if (player.chipStack >= toCall) {
        actions.push({ type: 'call', callAmount: toCall });
      }
      // If can't afford full call, all-in covers it
    }

    // Raise: available when player has enough chips to raise
    if (player.chipStack > 0) {
      const minRaise = Math.max(bigBlind, this.state.lastRaiseAmount);
      const minRaiseTotal = highestBet + minRaise;
      const minRaiseAmount = minRaiseTotal - player.currentBet;

      if (player.chipStack > toCall && player.chipStack >= minRaiseAmount) {
        actions.push({
          type: 'raise',
          minAmount: minRaiseAmount,
          maxAmount: player.chipStack,
        });
      }
    }

    // All-in is always available when player has chips
    if (player.chipStack > 0) {
      actions.push({ type: 'all-in' });
    }

    return actions;
  }

  /**
   * Submit a betting action for the given player.
   */
  submitAction(playerId: string, action: PlayerAction): ActionResult {
    const player = this.findPlayer(playerId);
    if (!player) {
      return { success: false, error: 'Player not found.' };
    }
    if (this.state.handPhase === 'complete' || this.state.handPhase === 'pre-deal') {
      return { success: false, error: 'No hand in progress.' };
    }
    if (this.state.currentPlayerIndex < 0) {
      return { success: false, error: 'No current player.' };
    }
    if (this.state.players[this.state.currentPlayerIndex].id !== playerId) {
      return { success: false, error: 'Not your turn.' };
    }
    if (player.status !== 'active') {
      return { success: false, error: `Player status is ${player.status}, cannot act.` };
    }

    // Validate action is in valid set
    const validActions = this.getValidActions(playerId);
    const isValid = validActions.some(va => va.type === action.type);
    if (!isValid) {
      return { success: false, error: `Action '${action.type}' is not valid.` };
    }

    const highestBet = this.getHighestCurrentBet();
    let actionAmount = 0;

    switch (action.type) {
      case 'fold':
        player.status = 'folded';
        player.holeCards = [];
        break;

      case 'check':
        if (highestBet > player.currentBet) {
          return { success: false, error: 'Cannot check when there is a bet to call.' };
        }
        break;

      case 'call': {
        const toCall = Math.min(highestBet - player.currentBet, player.chipStack);
        player.chipStack -= toCall;
        player.currentBet += toCall;
        player.totalBetThisHand += toCall;
        actionAmount = toCall;
        if (player.chipStack === 0) {
          player.status = 'all-in';
        }
        break;
      }

      case 'raise': {
        const raiseAmount = action.amount;
        if (raiseAmount === undefined) {
          return { success: false, error: 'Raise amount is required.' };
        }

        const minRaise = Math.max(this.state.blindLevel.bigBlind, this.state.lastRaiseAmount);
        const minRaiseTotal = highestBet + minRaise;
        const minRaiseFromPlayer = minRaiseTotal - player.currentBet;

        if (raiseAmount < minRaiseFromPlayer) {
          return { success: false, error: `Raise must be at least ${minRaiseFromPlayer}.` };
        }
        if (raiseAmount > player.chipStack) {
          return { success: false, error: 'Not enough chips for this raise.' };
        }

        const newBet = player.currentBet + raiseAmount;
        const raiseSize = newBet - highestBet;
        this.state.lastRaiseAmount = raiseSize;

        player.chipStack -= raiseAmount;
        player.currentBet = newBet;
        player.totalBetThisHand += raiseAmount;
        actionAmount = raiseAmount;

        if (player.chipStack === 0) {
          player.status = 'all-in';
        }

        // Reset hasActed for all other active players (they need to respond to raise)
        for (const p of this.state.players) {
          if (p.id !== playerId && p.status === 'active') {
            p.hasActedThisRound = false;
          }
        }
        break;
      }

      case 'all-in': {
        const allInAmount = player.chipStack;
        const newBet = player.currentBet + allInAmount;

        // If this is a raise (new bet > highest bet), update lastRaiseAmount
        if (newBet > highestBet) {
          const raiseSize = newBet - highestBet;
          if (raiseSize >= this.state.lastRaiseAmount) {
            this.state.lastRaiseAmount = raiseSize;
            // Reset hasActed for other active players
            for (const p of this.state.players) {
              if (p.id !== playerId && p.status === 'active') {
                p.hasActedThisRound = false;
              }
            }
          }
        }

        player.chipStack = 0;
        player.currentBet = newBet;
        player.totalBetThisHand += allInAmount;
        player.status = 'all-in';
        actionAmount = allInAmount;
        break;
      }
    }

    player.hasActedThisRound = true;

    // Record action
    const record: ActionRecord = {
      playerId: player.id,
      playerName: player.name,
      action: action.type,
      amount: actionAmount,
      phase: this.state.handPhase,
      timestamp: Date.now(),
    };
    this.state.actionHistory.push(record);

    // Check if hand ends (all but one folded)
    const activePlayers = this.getPlayersInHand();
    if (activePlayers.length === 1) {
      return this.awardPotToLastPlayer(activePlayers[0], record);
    }

    // Check if betting round is complete
    const roundComplete = this.isBettingRoundComplete();
    if (roundComplete) {
      const result = this.advancePhase();
      return {
        success: true,
        action: record,
        roundComplete: true,
        handComplete: result.handComplete,
        showdown: result.showdown,
      };
    }

    // Move to next player
    this.advanceToNextPlayer();

    return { success: true, action: record, roundComplete: false, handComplete: false };
  }

  /**
   * Get the current player's ID, or null if no one needs to act.
   */
  getCurrentPlayer(): string | null {
    if (this.state.currentPlayerIndex < 0) return null;
    if (this.state.handPhase === 'complete' || this.state.handPhase === 'pre-deal' || this.state.handPhase === 'showdown') {
      return null;
    }
    const player = this.state.players[this.state.currentPlayerIndex];
    if (player.status !== 'active') return null;
    return player.id;
  }

  // ========================================================================
  // Betting Round Helpers
  // ========================================================================

  private getHighestCurrentBet(): number {
    let max = 0;
    for (const p of this.state.players) {
      if (p.status !== 'eliminated' && p.status !== 'folded') {
        if (p.currentBet > max) max = p.currentBet;
      }
    }
    return max;
  }

  /**
   * A betting round is complete when all active (non-folded, non-all-in)
   * players have acted and their bets are equalized.
   */
  private isBettingRoundComplete(): boolean {
    const activePlayers = this.state.players.filter(
      p => p.status === 'active'
    );

    // If no active players (all folded or all-in), round is complete
    if (activePlayers.length === 0) return true;

    // All active players must have acted
    if (!activePlayers.every(p => p.hasActedThisRound)) return false;

    // All active players' bets must be equalized
    const highestBet = this.getHighestCurrentBet();
    return activePlayers.every(p => p.currentBet === highestBet);
  }

  private advanceToNextPlayer(): void {
    const idx = this.nextActiveNonAllInSeat(this.state.currentPlayerIndex);
    this.state.currentPlayerIndex = idx;
  }

  // ========================================================================
  // Phase Transitions (Task 5.2)
  // ========================================================================

  /**
   * Advance to the next phase after a betting round completes.
   */
  private advancePhase(): { handComplete: boolean; showdown?: ShowdownResult } {
    // Calculate pots before moving to next phase
    this.calculatePots();

    // Reset for next betting round
    for (const p of this.state.players) {
      p.currentBet = 0;
      p.hasActedThisRound = false;
    }

    const playersInHand = this.getPlayersInHand();
    const activeNonAllIn = playersInHand.filter(p => p.status === 'active');

    switch (this.state.handPhase) {
      case 'pre-flop':
        // Deal flop
        this.dealCommunityCards(3);
        this.state.handPhase = 'flop';
        this.state.currentBettingRound = 'flop';
        this.state.lastRaiseAmount = this.state.blindLevel.bigBlind;
        if (activeNonAllIn.length <= 1) {
          // All players are all-in or only one active — run out remaining cards
          return this.runOutRemainingCards();
        }
        this.state.currentPlayerIndex = this.getFirstToActPostFlop();
        return { handComplete: false };

      case 'flop':
        this.dealCommunityCards(1);
        this.state.handPhase = 'turn';
        this.state.currentBettingRound = 'turn';
        this.state.lastRaiseAmount = this.state.blindLevel.bigBlind;
        if (activeNonAllIn.length <= 1) {
          return this.runOutRemainingCards();
        }
        this.state.currentPlayerIndex = this.getFirstToActPostFlop();
        return { handComplete: false };

      case 'turn':
        this.dealCommunityCards(1);
        this.state.handPhase = 'river';
        this.state.currentBettingRound = 'river';
        this.state.lastRaiseAmount = this.state.blindLevel.bigBlind;
        if (activeNonAllIn.length <= 1) {
          return this.runOutRemainingCards();
        }
        this.state.currentPlayerIndex = this.getFirstToActPostFlop();
        return { handComplete: false };

      case 'river':
        // Showdown
        return this.performShowdown();

      default:
        return { handComplete: false };
    }
  }

  /**
   * When all players are all-in, deal remaining community cards and go to showdown.
   */
  private runOutRemainingCards(): { handComplete: boolean; showdown?: ShowdownResult } {
    while (this.state.communityCards.length < 5) {
      const count = this.state.communityCards.length === 0 ? 3 : 1;
      this.dealCommunityCards(count);
    }
    // Update phase names
    this.state.handPhase = 'river';
    return this.performShowdown();
  }

  /**
   * Deal community cards: burn 1, then deal `count` cards.
   */
  private dealCommunityCards(count: number): void {
    // Burn one card
    this.state.deck = burnCard(this.state.deck);
    // Deal community cards
    for (let i = 0; i < count; i++) {
      const result = dealCard(this.state.deck);
      this.state.communityCards.push(result.card);
      this.state.deck = result.deck;
    }
  }

  /**
   * Award pot to the last remaining player (everyone else folded).
   */
  private awardPotToLastPlayer(winner: PlayerState, record: ActionRecord): ActionResult {
    this.calculatePots();
    // Pots are built from totalBetThisHand which already includes currentBet
    const totalWinnings = this.state.pots.reduce((sum, p) => sum + p.amount, 0);

    winner.chipStack += totalWinnings;

    // Record hand summary
    this.state.handHistory.push({
      handNumber: this.state.handNumber,
      winnerId: winner.id,
      winnerName: winner.name,
      potAmount: totalWinnings,
      playerCount: this.state.players.filter(p => p.status !== 'eliminated').length,
      phase: this.state.handPhase,
    });

    this.state.handPhase = 'complete';
    this.state.currentBettingRound = null;
    this.state.currentPlayerIndex = -1;
    this.state.pots = [];

    // Reset current bets
    for (const p of this.state.players) {
      p.currentBet = 0;
    }

    // Check for eliminations
    this.checkEliminations();

    return {
      success: true,
      action: record,
      handComplete: true,
      roundComplete: true,
    };
  }

  // ========================================================================
  // Pot Management (Task 6)
  // ========================================================================

  /**
   * Calculate main pot and side pots based on player contributions.
   * Uses the "all-in" method: sort all-in players by contribution,
   * create pots at each level.
   */
  private calculatePots(): void {
    // Collect all bets into contributions
    const contributions: { playerId: string; amount: number; status: PlayerStatus }[] = [];
    for (const p of this.state.players) {
      if (p.currentBet > 0) {
        // Find existing contribution or create new
        const existing = contributions.find(c => c.playerId === p.id);
        if (existing) {
          existing.amount += p.currentBet;
        } else {
          contributions.push({
            playerId: p.id,
            amount: p.totalBetThisHand,
            status: p.status,
          });
        }
      } else if (p.totalBetThisHand > 0 && !contributions.find(c => c.playerId === p.id)) {
        contributions.push({
          playerId: p.id,
          amount: p.totalBetThisHand,
          status: p.status,
        });
      }
    }

    // Build pots from scratch using all player contributions
    this.buildPots();
  }

  /**
   * Build pots from player totalBetThisHand values.
   * This creates main pot and side pots correctly.
   */
  private buildPots(): void {
    // Get all players who have contributed (non-zero totalBetThisHand)
    const contributors = this.state.players
      .filter(p => p.totalBetThisHand > 0 || p.status === 'active' || p.status === 'all-in')
      .filter(p => p.status !== 'eliminated');

    // Get eligible players (not folded, not eliminated)
    const eligiblePlayers = this.state.players.filter(
      p => p.status === 'active' || p.status === 'all-in'
    );

    if (eligiblePlayers.length === 0) {
      return;
    }

    // Get unique contribution levels from all-in players
    const allInLevels = this.state.players
      .filter(p => p.status === 'all-in' && p.totalBetThisHand > 0)
      .map(p => p.totalBetThisHand)
      .sort((a, b) => a - b);

    // Remove duplicates
    const uniqueLevels = Array.from(new Set(allInLevels));

    // Also include the max contribution level
    const maxContribution = Math.max(...this.state.players.map(p => p.totalBetThisHand), 0);

    const pots: PotInfo[] = [];
    let previousLevel = 0;

    for (const level of uniqueLevels) {
      if (level <= previousLevel) continue;

      const potAmount = this.state.players
        .filter(p => p.totalBetThisHand > 0 && p.status !== 'eliminated')
        .reduce((sum, p) => sum + Math.min(p.totalBetThisHand, level) - Math.min(p.totalBetThisHand, previousLevel), 0);

      const eligible = this.state.players
        .filter(p => (p.status === 'active' || p.status === 'all-in') && p.totalBetThisHand >= level)
        .map(p => p.id);

      if (potAmount > 0) {
        pots.push({
          amount: potAmount,
          eligiblePlayerIds: eligible,
          isMainPot: pots.length === 0,
        });
      }

      previousLevel = level;
    }

    // Remaining pot above highest all-in level
    if (maxContribution > previousLevel) {
      const potAmount = this.state.players
        .filter(p => p.totalBetThisHand > previousLevel && p.status !== 'eliminated')
        .reduce((sum, p) => sum + (Math.min(p.totalBetThisHand, maxContribution) - previousLevel), 0);

      const eligible = this.state.players
        .filter(p => (p.status === 'active' || p.status === 'all-in') && p.totalBetThisHand > previousLevel)
        .map(p => p.id);

      if (potAmount > 0) {
        pots.push({
          amount: potAmount,
          eligiblePlayerIds: eligible,
          isMainPot: pots.length === 0,
        });
      }
    }

    // If no all-in players, just one main pot
    if (pots.length === 0) {
      const totalPot = this.state.players.reduce((sum, p) => sum + p.totalBetThisHand, 0);
      if (totalPot > 0) {
        pots.push({
          amount: totalPot,
          eligiblePlayerIds: eligiblePlayers.map(p => p.id),
          isMainPot: true,
        });
      }
    }

    this.state.pots = pots;
  }

  // ========================================================================
  // Showdown and Winner Determination (Task 7)
  // ========================================================================

  /**
   * Perform showdown: evaluate hands, distribute pots, handle eliminations.
   */
  private performShowdown(): { handComplete: boolean; showdown: ShowdownResult } {
    this.state.handPhase = 'showdown';
    this.state.currentBettingRound = null;
    this.state.currentPlayerIndex = -1;

    // Ensure pots are calculated
    this.buildPots();

    // Collect uncollected bets into pots
    // (currentBet should already be in totalBetThisHand, but rebuild pots to be safe)

    const playersInHand = this.getPlayersInHand();

    // Reveal all hole cards
    for (const p of playersInHand) {
      p.showCards = true;
    }

    // Evaluate each player's best hand
    const playerResults: ShowdownPlayerResult[] = playersInHand.map(p => ({
      playerId: p.id,
      playerName: p.name,
      holeCards: [...p.holeCards],
      bestHand: findBestHand(p.holeCards, this.state.communityCards),
    }));

    // Distribute each pot
    const potDistributions: PotDistribution[] = [];
    let totalDistributed = 0;

    for (let i = 0; i < this.state.pots.length; i++) {
      const pot = this.state.pots[i];
      const eligibleResults = playerResults.filter(
        pr => pot.eligiblePlayerIds.includes(pr.playerId)
      );

      if (eligibleResults.length === 0) continue;

      // Find the best hand among eligible players
      let bestResult = eligibleResults[0];
      for (let j = 1; j < eligibleResults.length; j++) {
        if (compare(eligibleResults[j].bestHand, bestResult.bestHand) > 0) {
          bestResult = eligibleResults[j];
        }
      }

      // Find all players who tie with the best hand
      const winners = eligibleResults.filter(
        pr => compare(pr.bestHand, bestResult.bestHand) === 0
      );

      const winnerIds = winners.map(w => w.playerId);
      const amountPerWinner = Math.floor(pot.amount / winners.length);
      const remainder = pot.amount - amountPerWinner * winners.length;

      // Distribute to winners
      for (const winnerId of winnerIds) {
        const player = this.findPlayer(winnerId);
        if (player) {
          player.chipStack += amountPerWinner;
          totalDistributed += amountPerWinner;
        }
      }

      // Odd chip goes to player closest left of dealer
      let oddChipRecipient: string | undefined;
      if (remainder > 0) {
        oddChipRecipient = this.getOddChipRecipient(winnerIds);
        const oddPlayer = this.findPlayer(oddChipRecipient);
        if (oddPlayer) {
          oddPlayer.chipStack += remainder;
          totalDistributed += remainder;
        }
      }

      potDistributions.push({
        potIndex: i,
        potAmount: pot.amount,
        winnerIds,
        amountPerWinner,
        winningHand: bestResult.bestHand,
        oddChipRecipient,
      });
    }

    const showdownResult: ShowdownResult = {
      playerResults,
      potDistributions,
    };

    // Record hand summary
    const mainWinner = potDistributions.length > 0
      ? potDistributions[0].winnerIds[0]
      : playersInHand[0]?.id || '';
    const mainWinnerPlayer = this.findPlayer(mainWinner);
    const mainWinnerResult = playerResults.find(pr => pr.playerId === mainWinner);

    this.state.handHistory.push({
      handNumber: this.state.handNumber,
      winnerId: mainWinner,
      winnerName: mainWinnerPlayer?.name || '',
      winningHand: mainWinnerResult?.bestHand,
      potAmount: this.state.pots.reduce((sum, p) => sum + p.amount, 0),
      playerCount: this.state.players.filter(p => p.status !== 'eliminated').length,
      phase: 'showdown',
    });

    this.state.handPhase = 'complete';
    this.state.pots = [];

    // Reset current bets
    for (const p of this.state.players) {
      p.currentBet = 0;
    }

    // Check for eliminations
    this.checkEliminations();

    return { handComplete: true, showdown: showdownResult };
  }

  /**
   * Get the player closest to the left of the dealer among the given player IDs.
   * Used for odd chip distribution in split pots.
   */
  private getOddChipRecipient(playerIds: string[]): string {
    let idx = this.nextActiveSeat(this.state.dealerSeatIndex);
    const startIdx = idx;
    do {
      const player = this.state.players[idx];
      if (playerIds.includes(player.id)) {
        return player.id;
      }
      idx = this.nextActiveSeat(idx);
    } while (idx !== startIdx);
    return playerIds[0]; // fallback
  }

  /**
   * Check for player eliminations and game end.
   */
  private checkEliminations(): void {
    for (const p of this.state.players) {
      if (p.chipStack === 0 && p.status !== 'eliminated') {
        p.status = 'eliminated';
      }
    }

    const remaining = this.state.players.filter(p => p.status !== 'eliminated');
    if (remaining.length === 1) {
      this.state.gameStatus = { type: 'complete', winnerId: remaining[0].id };
    } else if (remaining.length > 1) {
      this.state.gameStatus = { type: 'betweenHands' };
    }
  }

  // ========================================================================
  // State Query Methods (Task 4)
  // ========================================================================

  getGameState(): PokerGameState {
    return { ...this.state };
  }

  getPlayerState(playerId: string): PlayerState | undefined {
    return this.state.players.find(p => p.id === playerId);
  }

  getVisibleState(playerId: string): VisibleGameState {
    const visiblePlayers: VisiblePlayerState[] = this.state.players.map(p => ({
      id: p.id,
      name: p.name,
      seatIndex: p.seatIndex,
      chipStack: p.chipStack,
      holeCards: p.id === playerId || p.showCards ? [...p.holeCards] : [],
      status: p.status,
      currentBet: p.currentBet,
      isDealer: p.isDealer,
      isSmallBlind: p.isSmallBlind,
      isBigBlind: p.isBigBlind,
      hasActedThisRound: p.hasActedThisRound,
      showCards: p.showCards,
    }));

    return {
      tableSize: this.state.tableSize,
      buyIn: this.state.buyIn,
      blindLevel: { ...this.state.blindLevel },
      players: visiblePlayers,
      dealerSeatIndex: this.state.dealerSeatIndex,
      smallBlindSeatIndex: this.state.smallBlindSeatIndex,
      bigBlindSeatIndex: this.state.bigBlindSeatIndex,
      handNumber: this.state.handNumber,
      handPhase: this.state.handPhase,
      communityCards: [...this.state.communityCards],
      pots: this.state.pots.map(p => ({ ...p, eligiblePlayerIds: [...p.eligiblePlayerIds] })),
      currentBettingRound: this.state.currentBettingRound,
      currentPlayerIndex: this.state.currentPlayerIndex,
      actionHistory: [...this.state.actionHistory],
      gameStatus: { ...this.state.gameStatus } as PokerGameStatus,
    };
  }

  getCommunityCards(): Card[] {
    return [...this.state.communityCards];
  }

  getPots(): PotInfo[] {
    return this.state.pots.map(p => ({ ...p }));
  }

  getBettingRound(): BettingRound | null {
    return this.state.currentBettingRound;
  }

  getHandPhase(): HandPhase {
    return this.state.handPhase;
  }

  getDealerPosition(): number {
    return this.state.dealerSeatIndex;
  }

  getBlindPositions(): { smallBlind: number; bigBlind: number } {
    return {
      smallBlind: this.state.smallBlindSeatIndex,
      bigBlind: this.state.bigBlindSeatIndex,
    };
  }

  isHandInProgress(): boolean {
    return this.state.handPhase !== 'pre-deal' && this.state.handPhase !== 'complete';
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type === 'complete';
  }

  getWinner(): string | null {
    if (this.state.gameStatus.type === 'complete') {
      return this.state.gameStatus.winnerId;
    }
    return null;
  }

  getActivePlayers(): PlayerState[] {
    return this.state.players.filter(p => p.status !== 'eliminated');
  }

  getEliminatedPlayers(): PlayerState[] {
    return this.state.players.filter(p => p.status === 'eliminated');
  }

  // ========================================================================
  // Hand Evaluation Delegates
  // ========================================================================

  evaluateHand(cards: Card[]): HandResult {
    return evaluate(cards);
  }

  compareHands(hand1: HandResult, hand2: HandResult): number {
    return compare(hand1, hand2);
  }

  // ========================================================================
  // Serialization (Task 9)
  // ========================================================================

  /**
   * Serialize complete game state to JSON string.
   */
  serialize(): string {
    return JSON.stringify(this.state);
  }

  /**
   * Deserialize JSON string to game state with validation.
   * Returns the state or an error object.
   */
  deserialize(json: string): PokerGameState | { error: string } {
    if (!json || typeof json !== 'string' || json.trim() === '') {
      return { error: 'Invalid input: empty or non-string value.' };
    }

    let parsed: any;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      return { error: `Invalid JSON: ${(e as Error).message}` };
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { error: 'Invalid game state: expected an object.' };
    }

    // Validate required fields
    const requiredFields: (keyof PokerGameState)[] = [
      'tableSize', 'buyIn', 'blindLevel', 'players', 'dealerSeatIndex',
      'smallBlindSeatIndex', 'bigBlindSeatIndex', 'handNumber', 'handPhase',
      'deck', 'communityCards', 'pots', 'currentPlayerIndex',
      'lastRaiseAmount', 'actionHistory', 'gameStatus', 'handHistory',
    ];

    for (const field of requiredFields) {
      if (!(field in parsed)) {
        return { error: `Missing required field: ${field}` };
      }
    }

    // Validate types
    if (typeof parsed.tableSize !== 'number' || parsed.tableSize < 2 || parsed.tableSize > 8) {
      return { error: 'Invalid tableSize: must be a number between 2 and 8.' };
    }
    if (typeof parsed.buyIn !== 'number' || parsed.buyIn <= 0) {
      return { error: 'Invalid buyIn: must be a positive number.' };
    }
    if (!parsed.blindLevel || typeof parsed.blindLevel.smallBlind !== 'number' || typeof parsed.blindLevel.bigBlind !== 'number') {
      return { error: 'Invalid blindLevel: must have numeric smallBlind and bigBlind.' };
    }
    if (parsed.blindLevel.smallBlind <= 0 || parsed.blindLevel.bigBlind <= 0) {
      return { error: 'Invalid blindLevel: blinds must be positive.' };
    }
    if (!Array.isArray(parsed.players) || parsed.players.length < 2 || parsed.players.length > 8) {
      return { error: 'Invalid players: must be an array of 2–8 players.' };
    }

    const validPhases: HandPhase[] = ['pre-deal', 'pre-flop', 'flop', 'turn', 'river', 'showdown', 'complete'];
    if (!validPhases.includes(parsed.handPhase)) {
      return { error: `Invalid handPhase: ${parsed.handPhase}` };
    }

    if (!Array.isArray(parsed.communityCards)) {
      return { error: 'Invalid communityCards: must be an array.' };
    }
    if (!Array.isArray(parsed.pots)) {
      return { error: 'Invalid pots: must be an array.' };
    }
    if (!Array.isArray(parsed.actionHistory)) {
      return { error: 'Invalid actionHistory: must be an array.' };
    }
    if (!Array.isArray(parsed.handHistory)) {
      return { error: 'Invalid handHistory: must be an array.' };
    }

    // Validate each player
    for (let i = 0; i < parsed.players.length; i++) {
      const p = parsed.players[i];
      if (!p || typeof p.id !== 'string' || typeof p.name !== 'string') {
        return { error: `Invalid player at index ${i}: must have string id and name.` };
      }
      if (typeof p.chipStack !== 'number' || p.chipStack < 0) {
        return { error: `Invalid player at index ${i}: chipStack must be a non-negative number.` };
      }
      const validStatuses: PlayerStatus[] = ['active', 'folded', 'all-in', 'eliminated', 'sitting-out'];
      if (!validStatuses.includes(p.status)) {
        return { error: `Invalid player at index ${i}: invalid status '${p.status}'.` };
      }
    }

    // Validate deck
    if (!parsed.deck || !Array.isArray(parsed.deck.cards) || !Array.isArray(parsed.deck.dealtCards) || !Array.isArray(parsed.deck.burnCards)) {
      return { error: 'Invalid deck: must have cards, dealtCards, and burnCards arrays.' };
    }

    // Validate gameStatus
    if (!parsed.gameStatus || typeof parsed.gameStatus.type !== 'string') {
      return { error: 'Invalid gameStatus: must have a type field.' };
    }
    const validStatusTypes = ['waiting', 'active', 'handInProgress', 'betweenHands', 'complete'];
    if (!validStatusTypes.includes(parsed.gameStatus.type)) {
      return { error: `Invalid gameStatus type: ${parsed.gameStatus.type}` };
    }

    return parsed as PokerGameState;
  }

  /**
   * Restore game state from a validated state object.
   */
  setGameState(state: PokerGameState): void {
    this.state = JSON.parse(JSON.stringify(state));
  }

  // ========================================================================
  // Private Helpers
  // ========================================================================

  private findPlayer(playerId: string): PlayerState | undefined {
    return this.state.players.find(p => p.id === playerId);
  }

  /**
   * Get all players still in the current hand (active or all-in, not folded/eliminated).
   */
  private getPlayersInHand(): PlayerState[] {
    return this.state.players.filter(
      p => p.status === 'active' || p.status === 'all-in'
    );
  }

  /**
   * Get all non-eliminated players.
   */
  private getActivePlayersList(): PlayerState[] {
    return this.state.players.filter(p => p.status !== 'eliminated');
  }

  private createEmptyState(): PokerGameState {
    return {
      tableSize: 2,
      buyIn: DEFAULT_BUY_IN,
      blindLevel: { ...DEFAULT_BLIND_LEVEL },
      players: [],
      dealerSeatIndex: 0,
      smallBlindSeatIndex: -1,
      bigBlindSeatIndex: -1,
      handNumber: 0,
      handPhase: 'pre-deal',
      deck: createDeck(),
      communityCards: [],
      pots: [],
      currentBettingRound: null,
      currentPlayerIndex: -1,
      lastRaiseAmount: 0,
      actionHistory: [],
      gameStatus: { type: 'waiting' },
      handHistory: [],
    };
  }
}
